// Maps legacy icon classes (simple-line-icons `icon-*`, font-awesome `fa fa-*`)
// to Material Symbols Rounded ligature names, so the sidebar/header can
// render the new icon set without changing the shape of the nav data.
//
// The authoritative source for these values is the BACKEND, not any local
// frontend asset: `assets/roleModel/superadmin.json` is a static, hand
// maintained mock of what `GET /api/usergroup/get_rights_by_username`
// actually returns (see vowel-backend's MenuHeaderMaster/RightMaster
// entities and their Liquibase seed data under
// src/main/resources/config/liquibase/changelog/, notably
// menuheadermasterdata.sql, rightmasterdata.sql and the later corrective
// update-menu-icons.sql). Both sources use the same `icon-xxx`
// simple-line-icons naming convention — the map below was cross-checked
// against every distinct `icon-*` value actually present in those backend
// changelogs (plus the couple of icons the Java service code injects
// itself, e.g. the synthetic Dashboard/Reset-Password-Request rows), not
// just the ones that happened to appear in the frontend's stale mock —
// so it stays correct for real, non-SUPER_ADMIN accounts whose nav comes
// from that live endpoint, not the mock file.
export const ICON_MAP: { [legacyClass: string]: string } = {
  // Confirmed against vowel-backend's Liquibase changelogs / seed data
  'icon-speedometer': 'space_dashboard',
  'icon-home': 'home',
  'icon-settings': 'settings',
  'icon-user': 'person',
  'icon-user-follow': 'person_add',
  'icon-people': 'group',
  'icon-organization': 'account_tree',
  'icon-layers': 'layers',
  'icon-badge': 'badge',
  'icon-cursor': 'chevron_right',
  'icon-bell': 'notifications',
  'icon-chart': 'bar_chart',
  'icon-pie-chart': 'donut_small',
  'icon-login': 'login',
  'icon-logout': 'logout',
  'icon-star': 'star',
  'icon-flag': 'flag',
  'icon-ban': 'block',
  'icon-close': 'close',
  'icon-plus': 'add',
  'icon-refresh': 'refresh',
  'icon-clock': 'schedule',
  'icon-calendar': 'calendar_month',
  'icon-note': 'description',
  'icon-action-undo': 'undo',
  'icon-wrench': 'build',

  // Legacy values seen only in the frontend's local mock / dead _nav.ts,
  // not currently present in the backend's seed data — kept as a safety
  // net in case an older cached role JSON on a device still uses them.
  'icon-puzzle': 'apartment',
  'icon-key': 'vpn_key',
  'icon-clipboard': 'assignment',
  'icon-speech': 'chat',
  'icon-graph': 'insights',

  'fa fa-bell': 'notifications',
  'fa fa-user': 'person',
  'fa fa-wrench': 'build',
  'fa fa-lock': 'lock',
  'fa fa-mobile': 'smartphone',
};

// Legacy classnames (from the live backend, via the map above) are the
// common case, but a handful of JSON-only nav blocks (superadmin.json's
// DEVICE_ADMIN_* sections, hand-authored rather than backend-seeded) now
// carry real Material Symbols names directly instead of forcing every new
// icon through an invented, backend-ungrounded 'icon-xxx' key. Anything
// that isn't a recognized legacy prefix passes through unchanged; only a
// genuinely unmapped legacy class still falls back to 'circle'.
export function toMaterialIcon(legacyClass: string): string {
  if (!legacyClass) {
    return 'circle';
  }
  if (legacyClass in ICON_MAP) {
    return ICON_MAP[legacyClass];
  }
  if (legacyClass.startsWith('icon-') || legacyClass.startsWith('fa ') || legacyClass.startsWith('fa-')) {
    return 'circle';
  }
  return legacyClass;
}
